import { Box, Flex, Text, IconButton } from '@chakra-ui/react'
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons'
import { useState } from 'react'

interface DayCarouselProps {
  allDays: Date[]
  selectedDate: string
  onDateSelect: (date: Date) => void
  visibleDays: number
  availableDays?: string[]
  previousCacheStartDate?: string
  cacheDayRange?: number
}

const DayCarousel: React.FC<DayCarouselProps> = ({
  allDays,
  selectedDate,
  onDateSelect,
  visibleDays,
  availableDays = [],
  previousCacheStartDate,
  cacheDayRange,
}) => {
  const [startIndex, setStartIndex] = useState(0)

  const canScrollLeft = startIndex > 0
  const canScrollRight = startIndex + visibleDays < allDays.length

  const scrollDistance = visibleDays

  // Possibly scroll 7 days instead
  const handleScrollLeft = () => {
    setStartIndex(Math.max(0, startIndex - scrollDistance))
  }

  const handleScrollRight = () => {
    setStartIndex(
      Math.min(allDays.length - visibleDays, startIndex + scrollDistance)
    )
  }

  const onClickDate = (date: Date) => {
    onDateSelect(date)
    centerSelectedDate(date)
  }

  const centerSelectedDate = (selectedDate: Date) => {
    const selectedIndex = allDays.findIndex(
      (day) =>
        day.toISOString().split('T')[0] ===
        selectedDate.toISOString().split('T')[0]
    )

    if (selectedIndex !== -1) {
      const idealStartIndex = selectedIndex - Math.floor(visibleDays / 2)
      const maxStartIndex = Math.max(0, allDays.length - visibleDays)
      // Handles edge cases when selected date is towards end of array
      const possibleStartIndex = Math.min(idealStartIndex, maxStartIndex)

      // Handles edge cases when selected date is towards beginning of array
      const newStartIndex = Math.max(0, possibleStartIndex)

      setStartIndex(newStartIndex)
    }
  }

  const formatDate = (dateStr: Date) => {
    const date = new Date(dateStr)
    return {
      dayName: date.toLocaleDateString('en-US', { weekday: 'short' }),
      dayNumber: date.getDate(),
    }
  }

  const daysToMap = allDays.slice(startIndex, startIndex + visibleDays)

  return (
    <Flex align="center" justify="center" mb={6}>
      <IconButton
        variant="ghost"
        aria-label="Previous days"
        icon={<ChevronLeftIcon />}
        onClick={handleScrollLeft}
        isDisabled={!canScrollLeft}
      />
      <Flex mx={4}>
        {daysToMap.map((date) => {
          const { dayName, dayNumber } = formatDate(date)
          const formattedDate = date.toISOString().split('T')[0]
          const isSelected = formattedDate == selectedDate
          const hasAvailableSlots = availableDays.includes(formattedDate)

          // Check if date is within cache range
          let isInCacheRange = false

          if (previousCacheStartDate && cacheDayRange) {
            const cacheStartDateObj = new Date(
              previousCacheStartDate + 'T00:00:00'
            )
            const currentDateObj = new Date(formattedDate + 'T00:00:00')
            const daysDiff = Math.floor(
              (currentDateObj.getTime() - cacheStartDateObj.getTime()) /
                (1000 * 60 * 60 * 24)
            )
            isInCacheRange = daysDiff >= 0 && daysDiff < cacheDayRange
          }

          // A date is considered available if it has slots OR if it's outside the cache range (we don't know yet)
          const isAvailable = hasAvailableSlots || !isInCacheRange

          return (
            <Box
              key={formattedDate}
              onClick={() => onClickDate(date)}
              cursor="pointer"
              bg={isSelected ? '#4678E6' : '#FFFFFF'}
              color={isSelected ? 'white' : isAvailable ? 'black' : 'gray.500'}
              borderRadius="md"
              p={1}
              mx={1}
              textAlign="center"
              width="2.5rem"
              height="3.5rem"
              opacity={isAvailable ? 1 : 0.7}
              border={isAvailable ? '1px solid #E2E8F0' : '1px dashed #CBD5E0'}
              _hover={{
                bg: isSelected
                  ? '#4678E6'
                  : isAvailable
                  ? 'gray.200'
                  : 'gray.100',
              }}
              title={
                isAvailable
                  ? undefined
                  : 'Click to search for appointments on this date'
              }
            >
              <Text fontSize="xs" fontWeight="medium" margin="0">
                {dayName}
              </Text>
              <Text fontSize="md" fontWeight="bold" margin="0">
                {dayNumber}
              </Text>
            </Box>
          )
        })}
      </Flex>
      <IconButton
        variant="ghost"
        aria-label="Next days"
        icon={<ChevronRightIcon />}
        onClick={handleScrollRight}
        isDisabled={!canScrollRight}
      />
    </Flex>
  )
}

export default DayCarousel
